import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertCircle, Download, Loader2 } from "lucide-react";
import type {
  DedupResolutionItem,
  ImportAddressFix,
  ImportJob,
  ImportReviewDecision,
} from "@adva/types";
import {
  fetchAllImportJobDedupCandidates,
  useDownloadUnmatchedCsv,
  useImportJob,
  useImportJobAddressFixes,
  useImportJobDedupCandidates,
  useImportJobErrors,
  useResolveDedupCandidates,
  useSubmitImportReview,
} from "@/hooks/use-import";
import { PageHeader } from "@/components/v2/page-header";
import { KPIRow, type KPICardData } from "@/components/v2/kpi-row";
import { DetailHero } from "@/components/v2/detail-hero";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  IMPORT_STATUS_LABELS,
  deriveImportSituation,
  formatRelativeTime,
  formatEntityType,
  friendlyParentEntity,
  type ImportBanner,
} from "../labels";
import { HIGH_CONFIDENCE_THRESHOLD, pluralize } from "./labels";
import { AllChangesTab } from "./tabs/all-changes-tab";
import { AddressFixesTab } from "./tabs/address-fixes-tab";
import { DuplicatesTab, type DedupAction } from "./tabs/duplicates-tab";
import { ErrorsTab, type RowVerdict } from "./tabs/errors-tab";
import { MapTab } from "./tabs/map-tab";
import { UnmatchedTab } from "./tabs/unmatched-tab";
import { DiagnosisPanel } from "./components/diagnosis-panel";

function toApiResolutionAction(
  action: DedupAction,
): DedupResolutionItem["action"] {
  switch (action) {
    case "merge_a":
      return "merge_keep_a";
    case "merge_b":
      return "merge_keep_b";
    case "keep_both":
      return "keep_both";
    case "dismiss":
      return "dismiss";
  }
}

// `dedup_resolutions` on the review payload accepts only "merge_a" / "merge_b"
// / "keep_both" — dismissals are handled out-of-band via the resolve endpoint.
function toReviewResolution(
  action: DedupAction,
): "merge_a" | "merge_b" | "keep_both" | null {
  if (action === "dismiss") return null;
  return action;
}

const TAB_IDS = {
  allIssues: "all-changes",
  unmatched: "unmatched",
  errors: "errors",
  duplicates: "duplicates",
  addressFixes: "address-fixes",
  map: "map",
} as const;

export default function V2ImportReviewPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: jobResp, isLoading: jobLoading } = useImportJob(id || null);
  const { data: fixesResp } = useImportJobAddressFixes(id || null);
  const { data: errorsResp } = useImportJobErrors(id || null);
  const { data: dedupResp } = useImportJobDedupCandidates(id || null);

  const submitReview = useSubmitImportReview(id);
  const resolveDedup = useResolveDedupCandidates(id);
  const downloadUnmatched = useDownloadUnmatchedCsv();

  const [unchecked, setUnchecked] = useState<Set<string>>(new Set());
  const [resolutions, setResolutions] = useState<Map<string, DedupAction>>(
    new Map(),
  );
  const [skipped, setSkipped] = useState<Set<number>>(new Set());
  // ADV-1402: inline error-row corrections. Maps a row index to a record of
  // edited field → value (a single row can have multiple errored fields);
  // emitted as `corrected_rows` in the approve payload (skip wins, so skipped
  // rows are excluded).
  const [corrections, setCorrections] = useState<
    Map<number, Record<string, string>>
  >(new Map());
  // ADV-1402 (R3): per-row revalidation verdict, lifted from ErrorsTab so
  // approvability can require a confirmed-valid dry run — a pending ("checking")
  // or "invalid" verdict must NOT make a corrected row count toward approval.
  const [verdicts, setVerdicts] = useState<Map<number, RowVerdict>>(new Map());
  const [discardOpen, setDiscardOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(TAB_IDS.allIssues);
  const [bulkBusy, setBulkBusy] = useState(false);

  const job: ImportJob | undefined = jobResp?.job;
  const fixes = useMemo(() => fixesResp?.fixes ?? [], [fixesResp?.fixes]);
  const errors = useMemo(() => errorsResp?.errors ?? [], [errorsResp?.errors]);
  const candidates = useMemo(
    () => dedupResp?.candidates ?? [],
    [dedupResp?.candidates],
  );

  const isAwaitingReview = job?.status === "awaiting_review";
  const isSubmitting = submitReview.isPending || resolveDedup.isPending;

  const situation = useMemo(
    () => (job ? deriveImportSituation(job) : null),
    [job],
  );

  // ADV-1108: dynamic card list. Only render buckets with values > 0; this
  // collapses the "0 created · 0 updated · 0 errors" wall when the user
  // really only needs to see "Unmatched: 26,745".
  const kpiCards = useMemo<KPICardData[]>(() => {
    if (!job) return [];
    const list: KPICardData[] = [];
    const approvable = job.created_count + job.updated_count;
    if (approvable > 0) {
      list.push({
        label: "Ready to approve",
        value: approvable.toLocaleString(),
        subtitle: "created + updated",
      });
    }
    if (job.unmatched_count > 0) {
      list.push({
        label: "Unmatched",
        value: job.unmatched_count.toLocaleString(),
        subtitle: "waiting on parent records",
      });
    }
    if (job.error_rows > 0) {
      list.push({
        label: "Errors",
        value: job.error_rows.toLocaleString(),
        subtitle: "row failures",
      });
    }
    const fixesTotal = fixesResp?.total ?? fixes.length;
    if (fixesTotal > 0) {
      list.push({
        label: "Address fixes",
        value: fixesTotal.toLocaleString(),
      });
    }
    const dupesTotal = dedupResp?.total ?? candidates.length;
    if (dupesTotal > 0) {
      list.push({
        label: "Duplicates",
        value: dupesTotal.toLocaleString(),
      });
    }
    if (job.duplicates_dropped > 0) {
      list.push({
        label: "Dup-cleared emails",
        value: job.duplicates_dropped.toLocaleString(),
        subtitle: "imported without email",
      });
    }
    return list;
  }, [
    job,
    fixes.length,
    fixesResp?.total,
    candidates.length,
    dedupResp?.total,
  ]);

  function toggleAddressFix(locationId: string) {
    setUnchecked((prev) => {
      const next = new Set(prev);
      if (next.has(locationId)) {
        next.delete(locationId);
      } else {
        next.add(locationId);
      }
      return next;
    });
  }

  function acceptAllAddressFixes() {
    setUnchecked(new Set());
  }

  function toggleSkipError(rowIndex: number) {
    setSkipped((prev) => {
      const next = new Set(prev);
      if (next.has(rowIndex)) {
        next.delete(rowIndex);
      } else {
        next.add(rowIndex);
      }
      return next;
    });
  }

  function correctError(rowIndex: number, field: string, value: string) {
    // ADV-1402 (F2): the row's confirmed verdict is only valid for the value
    // that was revalidated. Any change to a corrected field must invalidate the
    // stale verdict so the footer stops counting the row as fixed and the F1
    // payload filter drops it until the blur-triggered dry run re-establishes a
    // "valid" verdict. Compare against the current value so an identical
    // re-entry (no real change) does not needlessly clear a valid verdict.
    const prevValue = corrections.get(rowIndex)?.[field];
    setCorrections((prev) => {
      const next = new Map(prev);
      const row = { ...(next.get(rowIndex) ?? {}) };
      // ADV-1402 (F4): every edit is explicit and is tracked, INCLUDING
      // clearing the field to blank. Clearing an invalid optional/nullable
      // value is a legitimate fix, so a blank ("") is a real correction that
      // must flow through the import coercion path on revalidate + approve
      // (blank is applied and re-validated), not silently dropped. Only a
      // field the reviewer never touched is omitted from `corrected_rows`.
      row[field] = value;
      next.set(rowIndex, row);
      return next;
    });
    if (prevValue !== value) {
      setRowVerdict(rowIndex, null);
    }
  }

  // ADV-1402 (R3): record the ErrorsTab dry-run verdict for a row. Drives
  // approvability below — only a "valid" verdict counts a corrected row as
  // resolved.
  function setRowVerdict(rowIndex: number, verdict: RowVerdict | null) {
    setVerdicts((prev) => {
      const next = new Map(prev);
      if (verdict === null) next.delete(rowIndex);
      else next.set(rowIndex, verdict);
      return next;
    });
  }

  function setResolution(candidateId: string, action: DedupAction) {
    setResolutions((prev) => {
      const next = new Map(prev);
      next.set(candidateId, action);
      return next;
    });
  }

  async function mergeAllHighConfidence() {
    setBulkBusy(true);
    try {
      const all = id ? await fetchAllImportJobDedupCandidates(id) : candidates;
      setResolutions((prev) => {
        const next = new Map(prev);
        for (const c of all) {
          if (c.confidence >= HIGH_CONFIDENCE_THRESHOLD && !next.has(c.id)) {
            next.set(c.id, "merge_a");
          }
        }
        return next;
      });
    } catch {
      toast.error("Could not load all candidates for merge");
    } finally {
      setBulkBusy(false);
    }
  }

  async function dismissAllDuplicates() {
    setBulkBusy(true);
    try {
      const all = id ? await fetchAllImportJobDedupCandidates(id) : candidates;
      setResolutions((prev) => {
        const next = new Map(prev);
        for (const c of all) {
          next.set(c.id, "dismiss");
        }
        return next;
      });
    } catch {
      toast.error("Could not load all candidates for dismiss");
    } finally {
      setBulkBusy(false);
    }
  }

  function buildReviewChanges(includeReverts: boolean): {
    dedup_resolutions: {
      candidate_id: string;
      resolution: "merge_a" | "merge_b" | "keep_both";
    }[];
    address_reverts: string[];
    corrected_rows: { row: number; values: Record<string, unknown> }[];
    skipped_rows: { row: number }[];
  } {
    const dedup_resolutions: {
      candidate_id: string;
      resolution: "merge_a" | "merge_b" | "keep_both";
    }[] = [];
    for (const [candidateId, action] of resolutions) {
      const r = toReviewResolution(action);
      if (r != null) {
        dedup_resolutions.push({ candidate_id: candidateId, resolution: r });
      }
    }
    const address_reverts = includeReverts ? Array.from(unchecked) : [];
    // ADV-1402: skip wins — a row that is both edited and skipped is sent as a
    // skip only, never a correction.
    const skipped_rows = Array.from(skipped).map((row) => ({ row }));
    // ADV-1402 (F1): the approve payload carries ONLY confirmed-valid
    // corrections. `verdicts` is the single source of truth for "is this fix
    // approvable" (F2 invalidates it on every edit), and it already gates
    // `fixedCount` + approvability below. Build `corrected_rows` from the SAME
    // predicate so an "Approve all" enabled by one valid row can never smuggle a
    // second row whose fix is still pending/invalid into the payload. Skipped
    // rows are excluded (skip wins).
    const corrected_rows: { row: number; values: Record<string, unknown> }[] =
      [];
    for (const [row, values] of corrections) {
      if (skipped.has(row)) continue;
      if (verdicts.get(row) !== "valid") continue;
      corrected_rows.push({ row, values });
    }
    return { dedup_resolutions, address_reverts, corrected_rows, skipped_rows };
  }

  // Dismiss-style dedup resolutions go through the resolve endpoint, not
  // the review payload. Sync them up-front so the workflow's review step
  // sees the canonical candidate state.
  async function syncDismissals(): Promise<void> {
    const dismissals: DedupResolutionItem[] = [];
    for (const [candidateId, action] of resolutions) {
      if (action === "dismiss") {
        dismissals.push({
          candidate_id: candidateId,
          action: toApiResolutionAction(action),
        });
      }
    }
    if (dismissals.length === 0) return;
    // The resolve endpoint caps `resolutions` at 500 per call
    // (ResolveDedupCandidatesRequestSchema.max(500)), so a full-import
    // "Dismiss all" (>500 candidates) must be sent in batches.
    const RESOLVE_BATCH_SIZE = 500;
    for (let i = 0; i < dismissals.length; i += RESOLVE_BATCH_SIZE) {
      await resolveDedup.mutateAsync(
        dismissals.slice(i, i + RESOLVE_BATCH_SIZE),
      );
    }
  }

  async function handleApproveAll() {
    if (!isAwaitingReview) return;
    try {
      await syncDismissals();
      const decision: ImportReviewDecision = {
        approved: true,
        changes: buildReviewChanges(false),
      };
      await submitReview.mutateAsync(decision);
      toast.success("Import approved");
      navigate("/import");
    } catch (e) {
      toast.error("Could not submit review", {
        description: e instanceof Error ? e.message : undefined,
      });
    }
  }

  async function handleApproveSelected() {
    if (!isAwaitingReview) return;
    const approvedRecordIds = fixes
      .map((f: ImportAddressFix) => f.location_id)
      .filter(
        (v: string | null): v is string =>
          typeof v === "string" && !unchecked.has(v),
      );
    try {
      await syncDismissals();
      const decision: ImportReviewDecision = {
        approved: true,
        partial: true,
        approved_record_ids: approvedRecordIds,
        changes: buildReviewChanges(true),
      };
      await submitReview.mutateAsync(decision);
      toast.success("Import partially approved", {
        description: pluralize(approvedRecordIds.length, "record"),
      });
      navigate("/import");
    } catch (e) {
      toast.error("Could not submit review", {
        description: e instanceof Error ? e.message : undefined,
      });
    }
  }

  function handleReviewLater() {
    toast.info("Review saved", {
      description: "We'll keep your import staged for 7 days.",
    });
    navigate("/import");
  }

  async function handleDiscard() {
    if (!isAwaitingReview) return;
    try {
      await submitReview.mutateAsync({ approved: false });
      toast.success("Import discarded");
      navigate("/import");
    } catch (e) {
      toast.error("Could not discard import", {
        description: e instanceof Error ? e.message : undefined,
      });
    }
  }

  function handleBannerCta(banner: ImportBanner) {
    if (!banner.cta) return;
    if (banner.cta.kind === "switch-tab") {
      setActiveTab(banner.cta.tabId);
      return;
    }
    if (banner.cta.kind === "download-unmatched" && job) {
      downloadUnmatched.mutate({
        jobId: job.id,
        filename: `${job.entity_type}-unmatched-${job.id}.csv`,
      });
    }
  }

  if (jobLoading || !job || !situation) {
    return (
      <>
        <PageHeader
          breadcrumbs={[
            { label: "Imports", href: "/import" },
            { label: "Review" },
          ]}
          title="Import Review"
        />
        <div className="flex items-center justify-center py-16">
          <Loader2
            className="size-5 animate-spin text-[color:var(--color-accent)]"
            aria-hidden="true"
          />
        </div>
      </>
    );
  }

  // ADV-1108: upload identity sub-line. ADV-1113 §1: lead the H1 with the
  // entity type and keep the subtitle to identity only — the state enum /
  // blocked label now lives in the banner + snapshot, not buried here.
  // Per-user attribution ("uploaded by …") and the original filename are
  // deferred — `ImportJob` carries neither (they live on `UploadedFile`); only
  // `source` + `api_key_id` are available. Tracked as a follow-up.
  const sourceLabel = job.source || formatEntityType(job.entity_type);
  const entityLabel = formatEntityType(job.entity_type);
  const headerSubtitle = [
    job.source,
    `uploaded ${formatRelativeTime(job.created_at)}`,
    `${job.total_rows.toLocaleString()} rows`,
  ]
    .filter(Boolean)
    .join(" • ");

  // ADV-1113 §5: one-sentence "what was uploaded" snapshot above the outcome
  // cards. Degrades gracefully — omits "via {source}" when source is missing.
  const snapshotLead = [
    `${job.total_rows.toLocaleString()} ${entityLabel} rows`,
    `uploaded ${formatRelativeTime(job.created_at)}`,
    job.source ? `via ${job.source}` : null,
  ]
    .filter(Boolean)
    .join(" ");
  const snapshotOutcome = (() => {
    const parts = [
      `${job.created_count.toLocaleString()} created`,
      `${job.updated_count.toLocaleString()} updated`,
    ];
    if (situation.unmatched > 0) {
      const fkField = job.unmatched_rows[0]?.field ?? null;
      const parentLabel = fkField ? friendlyParentEntity(fkField) : "parent";
      parts.push(
        `${situation.unmatched.toLocaleString()} held on missing ${parentLabel} parents`,
      );
    } else if (situation.held > 0) {
      parts.push(`${situation.held.toLocaleString()} held`);
    }
    return parts.join(" · ");
  })();

  // ADV-1402: surface pending error-row resolutions so the drop/fix is never
  // invisible at approve time. Skip wins, so a row that is both edited and
  // skipped counts only as skipped.
  // ADV-1402 (R3): a correction only counts as "fixed" once its dry-run
  // revalidation has COMPLETED and returned valid. A pending ("checking") or
  // "invalid" verdict — or a row that was typed but never revalidated — is NOT
  // counted, so it can neither show as fixed nor enable approval. This flows
  // into both the resolution summary and the approvability gate below.
  const fixedCount = Array.from(corrections.keys()).filter(
    (row) => !skipped.has(row) && verdicts.get(row) === "valid",
  ).length;
  const skippedCount = skipped.size;

  // Compute approvability before the Approve all button so the disabled-with-
  // reason title attribute reads naturally.
  // ADV-1402 (N4): `situation.approvable` is derived from the STALE pre-review
  // created_count + updated_count, so a job whose only actionable work is
  // fixable/skippable error rows would be stuck un-approvable. Fold the
  // reviewer's resolved error rows into approvability — approving fixed rows
  // re-imports them, and approving skipped rows records the audited drop and
  // finalizes. Only the genuinely-nothing-to-do case stays disabled.
  const hasErrorRowResolutions = fixedCount > 0 || skippedCount > 0;
  const nothingApprovable =
    situation.approvable === 0 && !hasErrorRowResolutions;
  const approveAllDisabledReason = !isAwaitingReview
    ? undefined
    : nothingApprovable
      ? "Nothing to approve — see issues below"
      : undefined;

  // Re-import CTA appears only when the only path forward is re-uploading
  // after the parent rows are loaded (no validation errors to fix in place).
  const showReimport = situation.unmatched > 0 && situation.errors === 0;

  const resolutionSummary =
    fixedCount > 0 || skippedCount > 0
      ? [
          fixedCount > 0 ? `${fixedCount} fixed` : null,
          skippedCount > 0 ? `${skippedCount} skipped` : null,
        ]
          .filter(Boolean)
          .join(" · ")
      : null;

  return (
    <>
      <PageHeader
        breadcrumbs={[
          { label: "Imports", href: "/import" },
          { label: sourceLabel },
        ]}
        title={`Import Review · ${entityLabel}`}
        subtitle={headerSubtitle}
      />

      {situation.banner && (
        <div className="mb-4 rounded-lg border border-[color:var(--color-warning)]/40 bg-[color:var(--color-warning)]/5 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-[color:var(--color-warning)]">
                <AlertCircle className="h-4 w-4" aria-hidden="true" />
                {situation.banner.title}
              </div>
              {situation.banner.body && (
                <p className="typo-body text-[color:var(--color-text-md)]">
                  {situation.banner.body}
                </p>
              )}
            </div>
            {situation.banner.cta && (
              <Button
                size="sm"
                variant="outline"
                disabled={
                  situation.banner.cta.kind === "download-unmatched" &&
                  downloadUnmatched.isPending
                }
                onClick={() => handleBannerCta(situation.banner!)}
              >
                {situation.banner.cta.kind === "download-unmatched" && (
                  <Download className="size-4" />
                )}
                {situation.banner.cta.label}
              </Button>
            )}
          </div>
        </div>
      )}

      {!isAwaitingReview && (
        <Alert className="mb-4">
          <AlertTitle>
            This import is in {IMPORT_STATUS_LABELS[job.status]} state
          </AlertTitle>
          <AlertDescription>
            Review actions are disabled. Showing read-only details below.
          </AlertDescription>
        </Alert>
      )}

      {/* ADV-1113 §5: plain-English "what was uploaded" snapshot above the
          outcome cards — answers "what is this?" before the buckets explain
          "where did the rows land". */}
      <p className="mb-4 typo-body text-[color:var(--color-text-md)]">
        {snapshotLead}.{" "}
        <span className="font-medium text-[color:var(--color-text-hi)]">
          {snapshotOutcome}.
        </span>
      </p>

      {kpiCards.length > 0 && (
        <div className="mb-6">
          <KPIRow cards={kpiCards} />
        </div>
      )}

      <div className="mb-6">
        <DiagnosisPanel jobId={job.id} status={job.status} />
      </div>

      <DetailHero
        variant="perennial"
        value={activeTab}
        onValueChange={setActiveTab}
        tabs={[
          {
            id: TAB_IDS.allIssues,
            label: "All issues",
            content: (
              <AllChangesTab
                fixes={fixes}
                candidates={candidates}
                errors={errors}
              />
            ),
          },
          {
            id: TAB_IDS.unmatched,
            label: "Unmatched",
            count: situation.unmatched,
            content: (
              <UnmatchedTab
                job={job}
                disabled={!isAwaitingReview || isSubmitting}
              />
            ),
          },
          {
            id: TAB_IDS.errors,
            label: "Errors",
            // Server-total row failures — `errors.length` is only the paged
            // first 50, which underreported the badge in the old screen.
            count: job.error_rows,
            content: (
              <ErrorsTab
                errors={errors}
                jobId={id}
                unmatchedCount={job.unmatched_count}
                entityType={job.entity_type}
                skipped={skipped}
                onToggleSkip={toggleSkipError}
                corrections={corrections}
                onCorrect={correctError}
                verdicts={verdicts}
                onVerdict={setRowVerdict}
                disabled={!isAwaitingReview || isSubmitting}
              />
            ),
          },
          {
            id: TAB_IDS.duplicates,
            label: "Duplicates",
            count: dedupResp?.total ?? candidates.length,
            content: (
              <DuplicatesTab
                candidates={candidates}
                resolutions={resolutions}
                onResolve={setResolution}
                onMergeAllHighConfidence={mergeAllHighConfidence}
                onDismissAll={dismissAllDuplicates}
                disabled={!isAwaitingReview || isSubmitting}
                bulkBusy={bulkBusy}
              />
            ),
          },
          {
            id: TAB_IDS.addressFixes,
            label: "Address fixes",
            count: fixesResp?.total ?? fixes.length,
            content: (
              <AddressFixesTab
                fixes={fixes}
                unchecked={unchecked}
                onToggle={toggleAddressFix}
                onAcceptAll={acceptAllAddressFixes}
                disabled={!isAwaitingReview || isSubmitting}
              />
            ),
          },
          {
            id: TAB_IDS.map,
            label: "Map",
            content: <MapTab fixes={fixes} />,
          },
        ]}
      />

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-[color:var(--color-border)] pt-4">
        <Button
          variant="ghost"
          className="text-[color:var(--color-danger)] hover:text-[color:var(--color-danger)]"
          onClick={() => setDiscardOpen(true)}
          disabled={!isAwaitingReview || isSubmitting}
        >
          Discard import
        </Button>
        <div className="flex flex-wrap items-center gap-2">
          {resolutionSummary && (
            <span className="typo-label text-[color:var(--color-text-md)]">
              {resolutionSummary}
            </span>
          )}
          <Button
            variant="outline"
            onClick={handleReviewLater}
            disabled={isSubmitting}
          >
            Review later
          </Button>
          {showReimport && (
            <Button
              variant="outline"
              onClick={() => navigate("/import")}
              disabled={isSubmitting}
            >
              Re-import →
            </Button>
          )}
          <Button
            variant="outline"
            onClick={handleApproveSelected}
            disabled={
              !isAwaitingReview ||
              isSubmitting ||
              bulkBusy ||
              unchecked.size === 0
            }
          >
            Approve selected
          </Button>
          <Button
            onClick={handleApproveAll}
            disabled={
              !isAwaitingReview || isSubmitting || bulkBusy || nothingApprovable
            }
            title={approveAllDisabledReason}
          >
            Approve all
          </Button>
        </div>
      </div>

      <AlertDialog open={discardOpen} onOpenChange={setDiscardOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard this import?</AlertDialogTitle>
            <AlertDialogDescription>
              All staged records will be deleted. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDiscard} disabled={isSubmitting}>
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
